import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'

import { ROUTES } from '@/constants'
import { ResizableSidebarLayout } from '@/pages/User/components/ResizableSidebarLayout'

import { BranchDetail } from './components/BranchDetail'
import { BranchesSidebar } from './components/BranchesSidebar'
import { BRANCHES, BranchRecord, getBranchesPath } from './branchesMockData'

type BranchesContextValue = {
	branches: BranchRecord[]
}

const BranchesContext = createContext<BranchesContextValue | null>(null)

export const useBranchesContext = () => {
	const context = useContext(BranchesContext)
	if (!context) {
		throw new Error('useBranchesContext must be used within BranchesLayout')
	}
	return context
}

export const BranchesLayout = () => {
	const navigate = useNavigate()
	const { branchId } = useParams<{ branchId?: string }>()

	const [branches, setBranches] = useState(BRANCHES)
	const [search, setSearch] = useState('')
	const [viewAsAdmin, setViewAsAdmin] = useState(true)
	const [activeBranchId] = useState<string | null>('feature-segment-risk')
	const [isCreateOpen, setCreateOpen] = useState(false)
	const [sourceType, setSourceType] = useState<'branch' | 'release'>('branch')
	const [sourceId, setSourceId] = useState('')
	const [name, setName] = useState('')
	const [description, setDescription] = useState('')

	const selectedBranch =
		branches.find((branch) => branch.id === branchId) ?? branches[0] ?? null

	const handleSelectBranch = useCallback(
		(id: string) => {
			navigate(getBranchesPath(id))
		},
		[navigate],
	)

	const closeCreate = useCallback(() => {
		setCreateOpen(false)
		setSourceType('branch')
		setSourceId('')
		setName('')
		setDescription('')
	}, [])

	const createBranch = useCallback(() => {
		const trimmedName = name.trim()
		if (!trimmedName || !sourceId) return
		const normalizedId = trimmedName
			.toLowerCase()
			.replace(/[^a-z0-9/\\-]+/g, '-')
			.replace(/--+/g, '-')
			.replace(/^-|-$/g, '')
		const newId = normalizedId || `branch-${Date.now()}`
		const sourceBranch = branches.find((branch) => branch.id === sourceId)
		const newBranch: BranchRecord = {
			id: newId,
			name: trimmedName,
			status: 'ACTIVE',
			system: sourceBranch?.system ?? 'Gasnet Data platform',
			sourceBranchId: sourceType === 'branch' ? sourceId : null,
			description: description.trim() || 'New branch created from current selection.',
			createdBy: 'You',
			createdAt: new Date().toLocaleDateString('en-GB'),
			protected: false,
			confidential: false,
			changedElements: [],
			releases: 0,
		}
		setBranches((prev) => [newBranch, ...prev])
		navigate(getBranchesPath(newId))
		closeCreate()
	}, [branches, closeCreate, description, name, navigate, sourceId, sourceType])

	const availableSources = useMemo(
		() =>
			branches.filter((branch) =>
				sourceType === 'branch' ? true : branch.status !== 'ARCHIVED',
			),
		[branches, sourceType],
	)

	const value = useMemo(() => ({ branches }), [branches])

	return (
		<BranchesContext.Provider value={value}>
			<Box sx={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden' }}>
				<ResizableSidebarLayout
					defaultWidth={300}
					localStorageKey="mdd-sidebar-width-branches"
					content={
						<Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
							<BranchDetail activeBranchId={activeBranchId} branch={selectedBranch} />
						</Box>
					}
				>
					<BranchesSidebar
						activeBranchId={activeBranchId}
						branchId={selectedBranch?.id ?? null}
						branches={branches}
						canCreate={viewAsAdmin}
						search={search}
						setSearch={setSearch}
						viewAsAdmin={viewAsAdmin}
						onOpenCreate={() => setCreateOpen(true)}
						onSelectBranch={handleSelectBranch}
						onToggleAdminView={setViewAsAdmin}
					/>
				</ResizableSidebarLayout>
			</Box>
			<Dialog
				open={isCreateOpen}
				onClose={closeCreate}
				maxWidth="md"
				fullWidth
				slotProps={{ paper: { sx: { borderRadius: '14px' } } }}
			>
				<DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: '#1F4954', pb: 1 }}>
					Create branch
				</DialogTitle>
				<DialogContent sx={{ pt: '10px !important' }}>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
							gap: 2,
						}}
					>
						<Box sx={{ gridColumn: '1 / -1' }}>
							<Typography
								sx={{
									mb: 1,
									fontSize: 11,
									fontWeight: 700,
									letterSpacing: '0.04em',
									textTransform: 'uppercase',
									color: '#9A9A9A',
								}}
							>
								Branch source
							</Typography>
							<Box sx={{ display: 'flex', gap: 1 }}>
								<Button
									variant={sourceType === 'branch' ? 'contained' : 'outlined'}
									onClick={() => {
										setSourceType('branch')
										setSourceId('')
									}}
									sx={{ textTransform: 'none', borderRadius: '8px', flex: 1 }}
								>
									From branch
								</Button>
								<Button
									variant={sourceType === 'release' ? 'contained' : 'outlined'}
									onClick={() => {
										setSourceType('release')
										setSourceId('')
									}}
									sx={{ textTransform: 'none', borderRadius: '8px', flex: 1 }}
								>
									From release
								</Button>
							</Box>
						</Box>
						<Box sx={{ gridColumn: '1 / -1' }}>
							<InputLabel
								sx={{
									mb: 0.75,
									fontSize: 12,
									fontWeight: 600,
									color: '#52666C',
								}}
							>
								{sourceType === 'branch' ? 'Source branch' : 'Release snapshot'}
							</InputLabel>
							<Select
								fullWidth
								size="small"
								value={sourceId}
								onChange={(event) => setSourceId(event.target.value)}
								displayEmpty
								sx={{ borderRadius: '9px', fontSize: 13 }}
							>
								<MenuItem value="" disabled>
									Select source...
								</MenuItem>
								{availableSources.map((branch) => (
									<MenuItem key={branch.id} value={branch.id} sx={{ fontSize: 13 }}>
										{branch.name}
									</MenuItem>
								))}
							</Select>
						</Box>
						<TextField
							label="Branch name"
							value={name}
							onChange={(event) => setName(event.target.value)}
							placeholder="feature/..."
							size="small"
							fullWidth
							slotProps={{ input: { sx: { borderRadius: '9px', fontSize: 13 } } }}
						/>
						<Box />
						<TextField
							label="Description"
							value={description}
							onChange={(event) => setDescription(event.target.value)}
							size="small"
							fullWidth
							multiline
							minRows={3}
							slotProps={{ input: { sx: { borderRadius: '9px', fontSize: 13 } } }}
							sx={{ gridColumn: '1 / -1' }}
						/>
					</Box>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5 }}>
					<Button onClick={closeCreate} sx={{ textTransform: 'none', borderRadius: '8px' }}>
						Cancel
					</Button>
					<Button
						variant="contained"
						disableElevation
						onClick={createBranch}
						disabled={!name.trim() || !sourceId}
						sx={{ textTransform: 'none', borderRadius: '8px', px: 2 }}
					>
						Create
					</Button>
				</DialogActions>
			</Dialog>
		</BranchesContext.Provider>
	)
}

export const BranchesEmbedded = () => (
	<Box sx={{ height: '100%', minHeight: 0 }}>
		<BranchesLayout />
	</Box>
)

export const getBranchPathOrRoot = (branchId?: string) =>
	branchId ? getBranchesPath(branchId) : ROUTES.BRANCHES
